import React, { useState, useEffect, useRef, useCallback } from 'react';
import Sidebar from './components/Sidebar';
import ChatHeader from './components/ChatHeader';
import ChatMessage from './components/ChatMessage';
import ChatInput from './components/ChatInput';
import HistorySection from './components/HistorySection';
import SaveOnLeaveDialog from './components/SaveOnLeaveDialog';
import DeleteTopicDialog from './components/DeleteTopicDialog';
import LoginScreen from './components/LoginScreen';
import { useSessions } from './hooks/useSessions';
import { useMessages } from './hooks/useMessages';
import { usePrompts } from './hooks/usePrompts';
import { useSidebarResize } from './hooks/useSidebarResize';
import { useDocuments } from './hooks/useDocuments';
import { useNavGuard } from './hooks/useNavGuard';
import { useChatSend } from './hooks/useChatSend';
import { SettingsContext } from './SettingsContext';
import * as api from './api/client';
import './styles.css';

export default function App() {
  // ── Auth state ─────────────────────────────────────────────────────────────
  const [currentUser, setCurrentUser] = useState(() => api.getStoredUser());
  // authReady gates rendering until we know whether the refresh token is valid.
  // Skip the async bootstrap when there's no stored user (go straight to login).
  const [authReady, setAuthReady] = useState(() => !api.getStoredUser());

  useEffect(() => {
    if (authReady) return;
    api.bootstrapAuth().then(ok => {
      if (!ok) {
        api.clearToken();
        setCurrentUser(null);
      }
      setAuthReady(true);
    });
  }, []);

  const handleLogin = useCallback(async (username, password) => {
    const data = await api.login(username, password);
    setCurrentUser({ user_id: data.user_id, username: data.username });
  }, []);

  const handleLogout = useCallback(() => {
    api.logout();
    setCurrentUser(null);
  }, []);

  if (!authReady) return null;

  // ── Show login screen if not authenticated ─────────────────────────────────
  if (!currentUser) {
    return (
      <div data-theme={typeof window !== 'undefined'
        ? (localStorage.getItem('theme') || 'light') : 'light'}>
        <LoginScreen onLogin={handleLogin} />
      </div>
    );
  }

  return <AuthenticatedApp currentUser={currentUser} onLogout={handleLogout} />;
}

// Separated so hooks only run when authenticated (avoids conditional hook calls)
function AuthenticatedApp({ currentUser, onLogout }) {
  const [activeId, setActiveId] = useState(null);
  const [tools, setTools]       = useState([]);
  const [input, setInput]       = useState('');

  const [modelName, setModelName]           = useState('');
  const [availableModels, setAvailableModels] = useState([]);
  const [llmBaseUrl, setLlmBaseUrl]         = useState('');
  const [imageSearchPageSize, setImageSearchPageSize] = useState(10);

  const [defaultSystemPrompt, setDefaultSystemPrompt] = useState('');
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'light');
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  const { sessions, refresh: refreshSessions, remove: removeSession, removeMany: removeSessions, update: updateSession } = useSessions(tools);
  const { sidebarWidth, collapsed, toggleCollapse, startResize } = useSidebarResize(320);
  const endRef    = useRef(null);
  const inputRef  = useRef(null);

  const activeSession      = sessions.find(s => s.id === activeId);
  const saveToKbByDefault  = activeSession?.saveToKbByDefault ?? false;
  const activeSystemPrompt = activeSession?.system_prompt ?? null;
  const activeToolConfig   = activeSession?.toolConfig ?? null;

  const { messages, load: loadMessages, clear: clearMessages,
        addMessage, appendToLastMessage, updateLastMessage,
        markLastExchangePersistent, togglePersistent
      } = useMessages(activeId);

  const { prompts, load: loadPrompts, clear: clearPrompts, save: savePrompt, remove: removePrompt } = usePrompts(activeId);
  const { documents, load: loadDocuments, clear: clearDocuments, uploadMany, ingestUrl, remove: removeDocument, reassess: reassessDocument } = useDocuments(activeId);

  const {
    saveDialogOpen,
    snapshotRef,
    guardedNavigate,
    handleDialogSave,
    handleDialogDiscard,
    handleDialogCancel,
    dismissDialog,
  } = useNavGuard(activeId, messages);

  const { loading, sendMessage } = useChatSend({
    activeId,
    setActiveId,
    messages,
    addMessage,
    appendToLastMessage,
    updateLastMessage,
    markLastExchangePersistent,
    loadPrompts,
    loadDocuments,
    refreshSessions,
    activeSystemPrompt,
    activeToolConfig,
    saveToKbByDefault,
  });

  // ── Load config + tools on mount ───────────────────────────────────────────
  useEffect(() => {
    api.getConfig().then(d => {
      setModelName(d.model_name);
      setAvailableModels(d.available_models ?? (d.model_name ? [d.model_name] : []));
      setLlmBaseUrl(d.llm_base_url ?? '');
      if (d.image_search_page_size) setImageSearchPageSize(d.image_search_page_size);
    }).catch(() => {});
    api.getTools().then(setTools).catch(() => {});
    api.getDefaultSystemPrompt().then(setDefaultSystemPrompt).catch(() => {});
  }, []);

  useEffect(() => { refreshSessions(); }, []);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ── Model switcher ─────────────────────────────────────────────────────────
  const handleModelChange = useCallback((model) => {
    api.setActiveModel(model)
      .then(() => setModelName(model))
      .catch((err) => console.error('Failed to switch model:', err));
  }, []);

  const handleNewChat = () => {
    guardedNavigate(() => {
      setActiveId(null);
      clearMessages();
      clearPrompts();
      clearDocuments();
    });
  };

  const handleSelectChat = (id) => {
    if (id === activeId) return;
    guardedNavigate(() => {
      setActiveId(id);
      loadMessages(id);
      loadPrompts(id);
      loadDocuments(id);
    });
  };

  const handleDeleteChat = (id) => {
    const session = sessions.find(s => s.id === id);
    setDeleteTarget({ id, name: session?.name || id });
  };

  const handleDeleteConfirm = async () => {
    const id = deleteTarget.id;
    setDeleteTarget(null);
    await removeSession(id);
    if (activeId === id) {
      dismissDialog();
      setActiveId(null);
      clearMessages();
      clearPrompts();
      clearDocuments();
    }
  };

  const handleDeleteChats = async (ids) => {
    await removeSessions(ids);
    if (ids.includes(activeId)) {
      dismissDialog();
      setActiveId(null);
      clearMessages();
      clearPrompts();
      clearDocuments();
    }
  };

  const handleClearHistory = async () => {
    if (!activeId) return;
    try {
      await api.clearHistory(activeId);
      loadMessages(activeId);
    } catch (err) {
      console.error('Failed to clear history', err);
    }
  };

  const handleSend = async (pendingFile = null) => {
    if ((!input.trim() && !pendingFile) || loading) return;
    const text = input;
    setInput('');
    inputRef.current?.focus();
    await sendMessage(text, pendingFile);
  };

  const handleSavePrompt  = async (content) => { if (activeId) await savePrompt(content); };
  const handleDeletePrompt = async (promptId) => removePrompt(promptId);

  const activeDisplayName = activeSession?.name || activeId;
  const historyMessages   = messages.filter(m => m.source === 'history');
  const sessionMessages   = messages.filter(m => m.source !== 'history');

  // Index of the last assistant message — it's the one being streamed into
  // while loading is true. We pass isStreaming=true only to that message so
  // MessageContent can mask incomplete image fences during streaming.
  const lastAssistantIndex = loading
    ? sessionMessages.reduce((found, msg, i) =>
        msg.role === 'assistant' ? i : found, -1)
    : -1;

  return (
    <SettingsContext.Provider value={{ imageSearchPageSize }}>
      <div className="app-layout">
        <Sidebar
          width={sidebarWidth}
          collapsed={collapsed}
          onToggleCollapse={toggleCollapse}
          onResizeStart={startResize}
          sessions={sessions}
          activeId={activeId}
          onNewChat={handleNewChat}
          onSelectChat={handleSelectChat}
          onDeleteChat={handleDeleteChat}
          onDeleteChats={handleDeleteChats}
          onUpdateSession={(id, s) => updateSession(id, s)}
          tools={tools}
          defaultSystemPrompt={defaultSystemPrompt}
          prompts={prompts}
          onDeletePrompt={handleDeletePrompt}
          onSelectPrompt={(content) => setInput(content)}
          documents={documents}
          onUploadDocument={uploadMany}
          onIngestUrl={ingestUrl}
          onDeleteDocument={removeDocument}
          onReassessDocument={reassessDocument}
          currentUser={currentUser}
          onLogout={onLogout}
        />

        <main className="chat-area">
          <ChatHeader
            title={activeDisplayName}
            theme={theme}
            onToggleTheme={() => setTheme(t => t === 'light' ? 'dark' : 'light')}
            modelName={modelName}
            llmBaseUrl={llmBaseUrl}
            availableModels={availableModels}
            onModelChange={handleModelChange}
          />

          <div className="messages-container">
            {historyMessages.length > 0 && (
              <HistorySection
                key={activeId}
                messages={historyMessages}
                onTogglePersistent={togglePersistent}
                onSavePrompt={handleSavePrompt}
                sessionMessages={sessionMessages}
                onToggleSessionPersistent={(i) => togglePersistent(messages.indexOf(sessionMessages[i]))}
                onSaveSessionPrompt={handleSavePrompt}
                onClearHistory={handleClearHistory}
              />
            )}

            {sessionMessages.length === 0 && (
              <div className="welcome-screen">
                <h2 className="welcome-title">How can I help you today?</h2>
                <p className="welcome-sub">You can ask me anything, I'll try to help.</p>
              </div>
            )}

            {sessionMessages.map((msg, i) => (
              <ChatMessage
                key={`session-${i}`}
                message={msg}
                index={messages.indexOf(msg)}
                onTogglePersistent={togglePersistent}
                onSavePrompt={handleSavePrompt}
                isStreaming={i === lastAssistantIndex}
              />
            ))}

            <div ref={endRef} />
          </div>

          <ChatInput
            value={input}
            onChange={setInput}
            onSend={handleSend}
            loading={loading}
            inputRef={inputRef}
          />
        </main>

        {saveDialogOpen && (
          <SaveOnLeaveDialog
            messageCount={snapshotRef.current.length}
            onSave={handleDialogSave}
            onDiscard={handleDialogDiscard}
            onCancel={handleDialogCancel}
          />
        )}

        {deleteTarget && (
          <DeleteTopicDialog
            topicName={deleteTarget.name}
            onConfirm={handleDeleteConfirm}
            onCancel={() => setDeleteTarget(null)}
          />
        )}
      </div>
    </SettingsContext.Provider>
  );
}